<template>
    <detail-header pretitle="Products" :title="snapshot ? snapshot.name : '&nbsp;'" :editable="true" :editMode="editMode" :editRoles="editRoles"
        :fields="fields" v-model="product" @editClick="onEditClick" @deleteClick="onDeleteClick" @saveClick="onSaveClick"
        @cancelClick="onCancelClick" @closeClick="$router.push('/admin/products')">
    </detail-header>
    <div class="container-fluid">
        <div v-if="error" class="alert alert-danger" role="alert">
            {{ error }}
        </div>
        <div v-if="product">
            <div class="card catalog-reveal">
                <div class="card-header">
                    <h4 class="card-header-title">Product details</h4>
                    <span v-if="editMode" class="catalog-editing">Editing</span>
                    <span v-else-if="auditItems.length" class="catalog-audit" :title="auditItems.join('\n')">{{ auditItems[auditItems.length - 1] }}</span>
                </div>
                <property-grid v-if="!editMode" :groups="[{ fields: [...compactFields, ...wideFields] }]" :record="product" :monoFields="['sku']" />
                <div v-else class="catalog-edit">
                    <section class="catalog-edit-group">
                        <div class="row">
                            <form-fields :fields="compactFields" v-model="product" :editMode="editMode"
                                colField="col-12 col-md-6 col-xl-3" />
                            <form-fields :fields="wideFields" v-model="product" :editMode="editMode" colField="col-12" />
                        </div>
                    </section>
                </div>
            </div>
            <div class="card catalog-reveal catalog-reveal-2">
                <div class="card-header">
                    <h4 class="card-header-title">
                        Entitlements<span class="catalog-count">{{ productEntitlementList.length }}</span>
                    </h4>
                    <div v-if="$store.getters.userRoles.isAqferAdmin" class="d-flex gap-2">
                        <button class="btn btn-sm btn-white" :disabled="editDisable"
                            @click="showModal({ component: 'editProductEntitlement', data: { product: product, list: productEntitlementList } })"><i class="fa fa-pen me-1"></i>Edit</button>
                        <button class="btn btn-sm btn-primary"
                            @click="showModal({ component: 'addProductEntitlement', data: { product: product, list: productEntitlementList } })"><i class="fa fa-plus me-1"></i>Add entitlement</button>
                    </div>
                </div>
                <div v-if="productEntitlementList.length" class="card-body">
                    <Datatable :tableId="`${product.sku}EntilementTable`" :columns="columns" uniqueField="entitlement_id"
                        :data="productEntitlementList" :showLoading="false" :deletable="$store.getters.userRoles.isAqferAdmin" @deleteRows="showDelete" :compact="true"
                        :selectCheckboxes="$store.getters.userRoles.isAqferAdmin"
                        :formatColumn="formatColumn"
                        :detailColumn="{ renderBaseUrl: entitlementDetailUrl, renderParams: entitlementDetailParams, name: 'Entitlement' }" />
                </div>
                <div v-else class="catalog-empty">
                    <span class="catalog-empty-icon"><i class="fa fa-layer-group"></i></span>
                    <p>No entitlements are mapped to this product yet.</p>
                    <small v-if="$store.getters.userRoles.isAqferAdmin">Use <strong>Add entitlement</strong> to grant an allowance.</small>
                </div>
            </div>
        </div>
        <div v-else-if="!error" class="text-center">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
    </div>
</template>
<script>
import DetailHeader from '../../components/DetailHeader.vue';
import Datatable from '../../components/Datatable.vue';
import Fields from "../../utils/field-constants/productFields";
import FormFields from '../../components/FormFields.vue';
import PropertyGrid from '../../components/admin/PropertyGrid.vue';
import { mapActions } from 'vuex';
import { auditSummary, nameCell, versionCell } from '../../utils/catalogCells';
const AUDIT_FIELDS = ["created_by", "created_at", "updated_by", "updated_at"];
export default {
    name: "Product",
    components: { DetailHeader, FormFields, Datatable, PropertyGrid },
    data() {
        return {
            editMode: false,
            product: null,
            snapshot: null,
            error: null,
            fields: Fields,
            columns: [
                { data: "name", render: nameCell },
                { data: "version", defaultContent: "", render: (data, type, row) => versionCell(this.entitlementDetailParams(row).version, type) },
                { data: "quantity" },
                { data: "effective_date", title: "Effective Date" },

            ],
            formatColumn: [
                {
                    index: 5,
                    type: "date",
                }
            ], 
            editRoles: [1]
        }
    },
    async beforeMount() {
        if (!this.$store.state.admin.entitlements.length)
            this.$store.dispatch("admin/fetchEntitlements");
        await this.fetchProduct();
        if (this.product)
            this.fetchProductEntitlementList(this.product.product_id)
    },
    computed:{
        productEntitlementList(){
            return this.$store.state.admin.productEntitlementList
        },
        editDisable(){
            return this.productEntitlementList.length>0?false: true
        },
        compactFields() {
            return this.fields.filter((field) => !AUDIT_FIELDS.includes(field.name) && field.type !== "textarea");
        },
        wideFields() {
            return this.fields.filter((field) => field.type === "textarea");
        },
        auditItems() {
            return auditSummary(this.snapshot);
        }
    },
    methods: {
        ...mapActions({
            readProduct: "admin/readProduct",
            updateProduct: "admin/updateProduct",
            deleteProduct: "admin/deleteProduct",
            showModal: "showModal",
            getProductEntitlementList: "admin/getProductEntitlementList"
        }),
        setEditMode(mode) {
            this.editMode = mode;
            this.error = null;
        },
        async fetchProduct() {
            let response = {};
            if (!this.$route.params.data) {
                try {
                    response = await this.readProduct({ name: this.$route.params.name, version: this.$route.params.version });
                } catch (error) {
                    if (error.response.data.error)
                        this.error = error.response.data.error;
                    else this.error = error.message;
                }
            } else {
                response["data"] = JSON.parse(this.$route.params.data)
            }
            this.product = await response.data;
            this.snapshot = JSON.parse(JSON.stringify(this.product));
        },
        async fetchProductEntitlementList(productId) {
            let response;
            try {
                response = await this.getProductEntitlementList("product=" + productId);
            } catch (error) {
                if (error.response.data.error)
                    this.error = error.response.data.error;
                else this.error = error.message;
            }
        },
        onCancelClick() {
            this.setEditMode(false);
            this.product = JSON.parse(JSON.stringify(this.snapshot));
            this.error = null;
        },
        onEditClick() {
            this.setEditMode(true);
        },
        onDeleteClick() {
            const self = this;
            this.showModal({
                component: "confirm",
                data: {
                    title: "Delete Product",
                    message: `Are you sure you want to delete ${this.product.name}`,
                    async confirm() {
                        try {
                            const response = await self.deleteProduct({ name: self.snapshot.name, version: self.snapshot.version });
                            self.$router.push("/admin/products");
                            return response;
                        } catch (error) {
                            if (error.response.data.error)
                                self.error = error.response.data.error;
                            else self.error = error.message;
                            return;
                        }
                    }
                }
            })
        },
        async onSaveClick() {
            let self = this;
            let productObj = this.product;
            try {
                const response = await this.updateProduct({ name: this.snapshot.name, version: this.snapshot.version, data: productObj });
                if (productObj.version === '' || productObj.version == null)
                    productObj.version = this.snapshot.version;
                this.setEditMode(false);
                this.snapshot = JSON.parse(JSON.stringify(productObj));
                this.$router.replace(`/admin/products/${encodeURIComponent(productObj.name)}/versions/${productObj.version}`);
                this.$store.dispatch('addToast', {
                    message: "Product '" + productObj.name + "' updated successfully.",
                    type: "success"
                });
                const refreshed = await this.readProduct({ name: productObj.name, version: productObj.version }).catch(() => null);
                if (refreshed && refreshed.data) {
                    this.product = refreshed.data;
                    this.snapshot = JSON.parse(JSON.stringify(refreshed.data));
                }
            } catch (error) {
                if (error.response.data.error)
                    self.error = error.response.data.error;
                else self.error = error.message;
            }
        },
        entitlementDetailParams(data) {
            const entitlement = this.$store.state.admin.entitlements.find((item) => item.entitlement_id === data.entitlement_id);
            return { name: entitlement ? entitlement.name : data.name, version: entitlement ? entitlement.version : '' };
        },
        entitlementDetailUrl(data) {
            const { name, version } = this.entitlementDetailParams(data);
            return `/admin/entitlements/${encodeURIComponent(name)}/versions/${version}`;
        },
        showDelete(rows) {
            this.showModal({ component: "deleteProductEntitlements", data: { product: this.product, rows: rows } });
        }
    }
}
</script>
<style lang="scss">
@import './catalog.scss';
</style>